import {useReducer} from "react";
import reducer from "./reducer";
import DonationServiceContext, {internalState} from "./context";
import {execute} from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import {IDonationService} from "../../models/donation-service/response";
import {ICreateDonationService, IUpdateDonationService} from "../../models/donation-service/request";
import {IDonationServiceQuery} from "../../models/donation-service/query";

export interface IProps {
    children: React.ReactNode;
}

const DonationServiceContextProvider: React.FC<IProps> = (props) => {
    const [state, dispatch] = useReducer(reducer, internalState)

    const getData = async () => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
                const { data } = await EndPoints.donationService.getAllDonationServices(state.query);
                dispatch({ type: "SET_LIST", payload: { list: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
            },
            throwException: false,
        });
    };

    const getDetails = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
                const { data } = await EndPoints.donationService.getDonationService(id);
                dispatch({ type: "SET_DETAILS", payload: { details: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
            },
            throwException: false,
        });
    };

    const setDetails = async (data?: IDonationService) => {
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
    };

    const createDonationService = async (request: ICreateDonationService) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
                await EndPoints.donationService.createDonationService(request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
            },
            throwException: true,
        });
    };

    const updateDonationService = async (id: number, request: IUpdateDonationService) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
                await EndPoints.donationService.updateDonationService(id, request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
            },
            throwException: true,
        });
    };

    const deleteDonationService = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
                await EndPoints.donationService.deleteDonationService(id);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
            },
            throwException: true,
        });
    };

    const setSearch = (search?: string) => {
        dispatch({ type: "SET_SEARCH", payload: { search } });
    };

    const setQuery = (query: IDonationServiceQuery) => {
        dispatch({ type: "SET_QUERY", payload: { query } });
    };

    return (
        <DonationServiceContext.Provider
            value={{
                ...state,
                actions: {
                    getData,
                    getDetails,
                    setDetails,
                    createDonationService,
                    updateDonationService,
                    deleteDonationService,
                    setSearch,
                    setQuery,
                }
            }}
        >
            {props.children}
        </DonationServiceContext.Provider>
    )
}

export default DonationServiceContextProvider;